import { useState, useRef } from "react";
import { SEO } from "@/components/SEO";
import Navigation from "@/components/Navigation";
import { Footer } from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { LocalizedLink } from "@/components/LocalizedLink";
import { USAB2BInquiryForm } from "@/components/markets/USAB2BInquiryForm";
import {
  Building2,
  ShieldCheck,
  Ship,
  Award,
  Layers,
  Factory,
  HandshakeIcon,
  ArrowRight,
  CheckCircle2,
  Crown,
  Globe2,
  FileText,
  Lock,
} from "lucide-react";

const USA = () => {
  const [unlocked, setUnlocked] = useState(false);
  const logisticsRef = useRef<HTMLDivElement | null>(null);
  const formRef = useRef<HTMLDivElement | null>(null);

  const scrollToForm = () =>
    formRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });

  const handleUnlock = () => {
    setUnlocked(true);
    setTimeout(() => {
      logisticsRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 150);
  };
  const breadcrumbs = [
    { name: "Home", url: "/" },
    { name: "Markets", url: "/european-markets" },
    { name: "United States — B2B", url: "/markets/usa" },
  ];

  const advantages = [
    {
      icon: Crown,
      title: "Flagship REM7 Collection",
      body: "Tankless smart toilets and whirlpool systems engineered for five-star hospitality and luxury residential developments.",
    },
    {
      icon: ShieldCheck,
      title: "CE Certified · UL-Compatible Builds",
      body: "European-engineered hardware available in 110V/60Hz configurations for US installation on request.",
    },
    {
      icon: Factory,
      title: "OEM & White-Label Ready",
      body: "Custom finishes, private-label packaging, and bespoke configurations for developer and brand partners.",
    },
    {
      icon: Layers,
      title: "Container-Scale Logistics",
      body: "240 units per 40ft container. Optimized loading plans for multi-property rollouts and dealer networks.",
    },
  ];

  const buyerTypes = [
    {
      icon: Building2,
      title: "Hotels & Hospitality Groups",
      body: "Boutique resorts, luxury chains, and branded residences refreshing guest bathrooms at scale.",
    },
    {
      icon: HandshakeIcon,
      title: "Developers & Builders",
      body: "Multi-unit residential, high-rise condos, and ultra-luxury single-family developments.",
    },
    {
      icon: Globe2,
      title: "Distributors & Showrooms",
      body: "Authorized dealer programs for plumbing distributors, design showrooms, and luxury bath retailers.",
    },
    {
      icon: Award,
      title: "Architects & Designers",
      body: "Specification-grade hardware for trade professionals working on signature projects.",
    },
  ];

  return (
    <>
      <SEO
        title="Smart Toilets USA — B2B Wholesale Supplier"
        description="B2B wholesale supply of smart toilets and whirlpool bathtubs for US hotels, developers and distributors. MOQ from 50 units, OEM and container logistics."
        keywords="b2b smart toilet usa, wholesale luxury bathroom usa, smart toilet distributor america, hotel smart toilet supplier, hospitality bidet supplier usa, oem smart toilet manufacturer, REM7 wholesale, luxury bathroom supplier usa"
        
        breadcrumbs={breadcrumbs}
        structuredData={{
          "@context": "https://schema.org",
          "@type": "WebPage",
          name: "Aureum VII Trading — USA B2B Wholesale",
          description:
            "Luxury smart toilet and whirlpool wholesale program for United States hotels, developers, distributors, and design professionals. Minimum order 50 units.",
          url: "https://www.aureum7trading.com/en/markets/usa",
          inLanguage: "en-US",
          isPartOf: {
            "@type": "WebSite",
            name: "Aureum VII Trading",
            url: "https://www.aureum7trading.com",
          },
          about: {
            "@type": "Organization",
            name: "Aureum VII Trading",
            areaServed: [
              { "@type": "Country", name: "United States" },
              { "@type": "Country", name: "Canada" },
            ],
            makesOffer: {
              "@type": "Offer",
              eligibleCustomerType: "https://schema.org/Business",
              eligibleQuantity: {
                "@type": "QuantitativeValue",
                minValue: 50,
                unitText: "units",
              },
            },
          },
        }}
      />
      <Navigation />

      <main className="min-h-screen bg-gradient-to-b from-background via-background to-background/95">
        {/* Hero */}
        <section className="relative pt-32 pb-24 px-4 overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-transparent to-primary/10 pointer-events-none" />
          <div className="container mx-auto max-w-6xl relative">
            <div className="grid lg:grid-cols-12 gap-12 items-center">
              <div className="lg:col-span-7">
                <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/10 text-primary border border-primary/20 mb-6">
                  <span className="text-xl">🇺🇸</span>
                  <span className="text-sm font-medium tracking-wide uppercase">
                    United States · B2B Wholesale
                  </span>
                </div>

                <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold mb-6 leading-tight">
                  European Luxury.
                  <span className="block bg-gradient-to-r from-primary to-primary/70 bg-clip-text text-transparent">
                    American Scale.
                  </span>
                </h1>

                <p className="text-xl md:text-2xl text-muted-foreground mb-8 max-w-2xl leading-relaxed">
                  A B2B wholesale program for US hotels, developers, distributors, and
                  designers seeking the world's most refined smart bathroom systems —
                  delivered by the container.
                </p>

                <div className="flex flex-wrap gap-4 mb-8">
                  <Button size="lg" className="text-base" onClick={scrollToForm}>
                    Request B2B Quote
                    <ArrowRight className="ml-2 w-4 h-4" />
                  </Button>
                  <Button asChild size="lg" variant="outline" className="text-base">
                    <LocalizedLink to="/business">Wholesale Program Details</LocalizedLink>
                  </Button>
                </div>

                <div className="flex flex-wrap items-center gap-6 text-sm text-muted-foreground">
                  <div className="flex items-center gap-2">
                    <CheckCircle2 className="w-4 h-4 text-primary" />
                    <span>MOQ from 50 units</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <CheckCircle2 className="w-4 h-4 text-primary" />
                    <span>OEM & white-label available</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <CheckCircle2 className="w-4 h-4 text-primary" />
                    <span>Direct factory pricing</span>
                  </div>
                </div>
              </div>

              <div className="lg:col-span-5">
                <Card className="p-8 bg-gradient-to-br from-card to-card/50 border-primary/20 shadow-2xl">
                  <div className="flex items-center gap-3 mb-6">
                    <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
                      <FileText className="w-6 h-6 text-primary" />
                    </div>
                    <div>
                      <div className="text-sm text-muted-foreground">For Verified Buyers</div>
                      <div className="font-semibold">B2B Inquiry Includes</div>
                    </div>
                  </div>
                  <ul className="space-y-3 text-sm">
                    {[
                      "Wholesale price list (USD)",
                      "Full product catalog & spec sheets",
                      "Container loading & logistics breakdown",
                      "Lead time estimate for your order volume",
                      "OEM and custom finish options",
                      "Dealer / distributor program terms",
                    ].map((item) => (
                      <li key={item} className="flex items-start gap-2">
                        <CheckCircle2 className="w-4 h-4 text-primary mt-0.5 shrink-0" />
                        <span className="text-foreground">{item}</span>
                      </li>
                    ))}
                  </ul>
                  <Button className="w-full mt-6" onClick={scrollToForm}>
                    Start Inquiry
                  </Button>
                </Card>
              </div>
            </div>
          </div>
        </section>

        {/* Why partner with us */}
        <section className="py-20 px-4 border-t border-border/50">
          <div className="container mx-auto max-w-6xl">
            <div className="max-w-2xl mb-12">
              <div className="text-sm uppercase tracking-widest text-primary mb-3">
                Why Aureum VII
              </div>
              <h2 className="text-4xl md:text-5xl font-bold mb-4">
                Built for serious volume.
              </h2>
              <p className="text-lg text-muted-foreground">
                We supply the United States market on B2B terms only. Our partners build
                hotels, premium residences, and luxury showrooms — not single bathrooms.
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {advantages.map((a) => (
                <Card
                  key={a.title}
                  className="p-6 bg-card/50 border-border/50 hover:border-primary/40 transition-colors"
                >
                  <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center mb-4">
                    <a.icon className="w-6 h-6 text-primary" />
                  </div>
                  <h3 className="font-semibold text-lg mb-2">{a.title}</h3>
                  <p className="text-sm text-muted-foreground leading-relaxed">{a.body}</p>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* Who we work with */}
        <section className="py-20 px-4 bg-gradient-to-b from-transparent via-primary/[0.02] to-transparent">
          <div className="container mx-auto max-w-6xl">
            <div className="max-w-2xl mb-12">
              <div className="text-sm uppercase tracking-widest text-primary mb-3">
                Trade Partners
              </div>
              <h2 className="text-4xl md:text-5xl font-bold mb-4">
                Who we supply.
              </h2>
              <p className="text-lg text-muted-foreground">
                Our wholesale program serves trade buyers across the United States.
                Retail and single-unit inquiries are referred to authorized partners.
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              {buyerTypes.map((b) => (
                <Card
                  key={b.title}
                  className="p-8 bg-card/50 border-border/50 hover:border-primary/40 transition-colors flex gap-5"
                >
                  <div className="w-14 h-14 rounded-xl bg-primary/10 flex items-center justify-center shrink-0">
                    <b.icon className="w-7 h-7 text-primary" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-xl mb-2">{b.title}</h3>
                    <p className="text-muted-foreground leading-relaxed">{b.body}</p>
                  </div>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* B2B Inquiry Form */}
        <section ref={formRef} className="py-20 px-4 border-t border-border/50 scroll-mt-24">
          <div className="container mx-auto max-w-3xl">
            <div className="text-center mb-10">
              <div className="text-sm uppercase tracking-widest text-primary mb-3">
                Step 1 — Qualification
              </div>
              <h2 className="text-4xl md:text-5xl font-bold mb-4">
                Tell us about your project.
              </h2>
              <p className="text-lg text-muted-foreground">
                Delivery times, lead schedules, and logistics breakdowns are shared with
                qualified trade buyers only. Complete the inquiry below to unlock them.
              </p>
            </div>
            <USAB2BInquiryForm onSuccess={handleUnlock} />
          </div>
        </section>

        {/* Logistics & process — gated */}
        <section
          ref={logisticsRef}
          className="py-20 px-4 border-t border-border/50 scroll-mt-24"
        >
          <div className="container mx-auto max-w-6xl">
            {!unlocked ? (
              <Card className="p-10 lg:p-16 text-center bg-gradient-to-br from-card to-card/60 border-dashed border-primary/30">
                <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-6">
                  <Lock className="w-7 h-7 text-primary" />
                </div>
                <div className="text-sm uppercase tracking-widest text-primary mb-3">
                  Locked — Trade Buyers Only
                </div>
                <h2 className="text-3xl md:text-4xl font-bold mb-4">
                  Delivery & logistics details
                </h2>
                <p className="text-muted-foreground mb-8 max-w-xl mx-auto">
                  Production lead times, freight options, container loading plans, and
                  the full 5-step delivery process are revealed once your B2B inquiry
                  is submitted.
                </p>
                <Button size="lg" onClick={scrollToForm}>
                  Complete Inquiry to Unlock
                  <ArrowRight className="ml-2 w-4 h-4" />
                </Button>
              </Card>
            ) : (
              <div className="grid lg:grid-cols-2 gap-12 items-start">
                <div>
                  <div className="text-sm uppercase tracking-widest text-primary mb-3">
                    Logistics to the US
                  </div>
                  <h2 className="text-4xl md:text-5xl font-bold mb-6">
                    Shipped from Europe to your port.
                  </h2>
                  <p className="text-lg text-muted-foreground mb-8 leading-relaxed">
                    Aureum VII Trading is headquartered in Marbella, Spain. Every US
                    order is configured per project — port of entry, freight terms, and
                    delivery schedule are confirmed in your custom quote.
                  </p>
                  <div className="space-y-4">
                    {[
                      {
                        icon: Ship,
                        title: "Container & LCL options",
                        body: "Full container loads optimized for 240 units per 40ft, or consolidated shipments for smaller wholesale orders.",
                      },
                      {
                        icon: ShieldCheck,
                        title: "CE certified · Specification documentation",
                        body: "Full technical files, installation guides, and warranty terms provided for every unit.",
                      },
                      {
                        icon: HandshakeIcon,
                        title: "Dedicated B2B account manager",
                        body: "One point of contact from inquiry through after-sales support for the lifetime of the partnership.",
                      },
                    ].map((item) => (
                      <div key={item.title} className="flex gap-4">
                        <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
                          <item.icon className="w-5 h-5 text-primary" />
                        </div>
                        <div>
                          <div className="font-semibold mb-1">{item.title}</div>
                          <p className="text-sm text-muted-foreground">{item.body}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                <Card className="p-8 lg:p-10 bg-gradient-to-br from-primary/5 to-card border-primary/20">
                  <div className="text-sm uppercase tracking-widest text-primary mb-3">
                    Inquiry Process
                  </div>
                  <h3 className="text-2xl font-bold mb-6">From request to delivery.</h3>
                  <ol className="space-y-5">
                    {[
                      { n: "01", title: "Submit your B2B inquiry", body: "Tell us about your project, volume, and target timeline." },
                      { n: "02", title: "Receive a tailored quote", body: "Wholesale pricing in USD, lead time, and logistics plan within 48 hours." },
                      { n: "03", title: "Sample order or LOI", body: "Approve samples, sign letter of intent, and confirm production slot." },
                      { n: "04", title: "Production & shipping", body: "Manufacturing begins; weekly updates through delivery to your US port." },
                      { n: "05", title: "Installation & support", body: "Full documentation, warranty, and ongoing account management." },
                    ].map((step) => (
                      <li key={step.n} className="flex gap-4">
                        <div className="font-bold text-primary text-lg w-10 shrink-0">{step.n}</div>
                        <div>
                          <div className="font-semibold mb-1">{step.title}</div>
                          <p className="text-sm text-muted-foreground">{step.body}</p>
                        </div>
                      </li>
                    ))}
                  </ol>
                </Card>
              </div>
            )}
          </div>
        </section>

        {/* Final CTA */}
        <section className="py-24 px-4">
          <div className="container mx-auto max-w-4xl">
            <Card className="p-10 lg:p-16 bg-gradient-to-br from-primary/10 via-card to-card border-primary/30 text-center shadow-2xl">
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/10 text-primary border border-primary/20 mb-6">
                <Crown className="w-4 h-4" />
                <span className="text-sm font-medium tracking-wide uppercase">
                  Trade Inquiries Only
                </span>
              </div>
              <h2 className="text-4xl md:text-5xl font-bold mb-6">
                Bring European luxury to your US project.
              </h2>
              <p className="text-lg text-muted-foreground mb-8 max-w-2xl mx-auto">
                Tell us about your project. We'll respond within one business day with
                wholesale pricing, lead times, and a logistics plan tailored to your
                order.
              </p>
              <div className="flex flex-wrap justify-center gap-4">
                <Button size="lg" className="text-base" onClick={scrollToForm}>
                  Request B2B Quote
                  <ArrowRight className="ml-2 w-4 h-4" />
                </Button>
                <Button
                  asChild
                  size="lg"
                  variant="outline"
                  className="text-base"
                >
                  <a
                    href="https://wa.me/34688495309"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    WhatsApp Sales
                  </a>
                </Button>
              </div>
              <p className="text-xs text-muted-foreground mt-8">
                Minimum order quantity: 50 units · OEM and custom finishes available ·
                Wholesale terms only
              </p>
            </Card>
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
};

export default USA;
